import dayjs from 'dayjs';
import _ from 'lodash';

import {
  CicDisclosureFilter,
  ReasonEditKind,
  UNKNOWN_DELIVERY_STATUS_LABEL,
  CicDisclosureShortName,
  CicDisclosureTitle,
  DISCLOSURE_SHORT_NAME_FALLBACK,
  DELIVERY_STATUS_LABELS,
  WINDOW_CLOSED_LABEL,
  WINDOW_CLOSES_LABEL,
} from './LoanCicHistory.constants';

// `deliveryStatus` is Skynet.Forms' dotted request-status discriminator (`InProgress.AfterSending`),
// matched by prefix so `Cancelled` also covers `CancelledBySystem.*`.
export const getDeliveryStatusLabel = deliveryStatus =>
  DELIVERY_STATUS_LABELS.find(({ prefix }) => deliveryStatus?.startsWith(prefix))?.label ??
  UNKNOWN_DELIVERY_STATUS_LABEL;

/** Numbers each disclosure within its bundle type, oldest first, keeping the newest-first order. */
export const buildDisclosureRows = disclosures => {
  const countByType = _.countBy(disclosures, 'bundleType');
  const seenByType = {};

  return disclosures.map(disclosure => {
    const { bundleType, formDeliveryRequestId } = disclosure;

    seenByType[bundleType] = (seenByType[bundleType] ?? 0) + 1;

    return {
      ...disclosure,
      id: formDeliveryRequestId,
      title: `${CicDisclosureTitle[bundleType]} #${countByType[bundleType] - seenByType[bundleType] + 1}`,
    };
  });
};

export const filterDisclosureRows = (rows, filter) =>
  filter === CicDisclosureFilter.ALL ? rows : rows.filter(row => row.bundleType === filter);

export const getWindowLabel = (plannedTriggerDate, now = dayjs()) =>
  dayjs(plannedTriggerDate).isAfter(now) ? WINDOW_CLOSES_LABEL : WINDOW_CLOSED_LABEL;

export const getDisclosureShortName = bundleType =>
  CicDisclosureShortName[bundleType] ?? DISCLOSURE_SHORT_NAME_FALLBACK;

export const getPendingFootnote = bundleType =>
  `Changes saved before then roll into this ${getDisclosureShortName(bundleType)}`;

export const getIsHistoryEmpty = ({ pending, disclosures, removed, earlier, feeChanges } = {}) =>
  _.isNil(pending) &&
  _.isEmpty(disclosures) &&
  _.isEmpty(removed) &&
  _.isEmpty(earlier) &&
  _.isEmpty(feeChanges);

const getIsChangeEdited = change => !_.isEmpty(change?.revisions);

const getOriginalReasonName = ({ revisions, reasonName }) =>
  _.isEmpty(revisions) ? reasonName : _.first(revisions).previousReasonName;

/**
 * What the reason cell says about an edited change: the struck original when the reason now differs
 * from it, otherwise which part the latest edit touched (a reason edited back to the original reads
 * as a reason change, not a comment change).
 */
export const getReasonEditSummary = change => {
  if (!getIsChangeEdited(change)) {
    return null;
  }

  const latestRevision = _.last(change.revisions);
  const originalReasonName = getOriginalReasonName(change);
  const isLatestReasonEdit = latestRevision.previousCicReasonId !== latestRevision.newCicReasonId;

  const kind =
    (originalReasonName !== change.reasonName && ReasonEditKind.ORIGINAL_REPLACED) ||
    (isLatestReasonEdit && ReasonEditKind.REASON_CHANGED) ||
    ReasonEditKind.COMMENT_CHANGED;

  return {
    kind,
    originalReasonName,
    editedByName: latestRevision.changedByName,
    editedDate: latestRevision.changedDate,
  };
};

/** A carried field shows what the disclosure carried: the window's last disclosed value → its new one. */
export const toCarriedChange = ({ change, label, previousDisplay, newDisplay }) => ({
  ...change,
  label,
  oldDisplay: previousDisplay,
  newDisplay,
});
